/**
 * Form draft storage utility for persisting standard (non-conversational)
 * respondent progress in localStorage with a 24-hour expiration.
 */

const DRAFT_TTL_HOURS = 24;

export interface StoredFormDraftV1 {
  schemaVersion: 1;
  projectId: string;
  formId: string;
  version: number;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  startedAt: string; // ISO timestamp
  answers: Record<string, unknown>;
  currentStep: number;
  currentPageIndex: number;
  questionHistory: number[];
  pageHistory: number[];
  questionPath: string[];
}

export type StoredFormDraft = StoredFormDraftV1;

function getStorageKey(
  projectId: string,
  formId: string,
  version: number,
): string {
  return `kushya_draft_${projectId}_${formId}_${version}`;
}

function isDraftExpired(updatedAt: string): boolean {
  const updated = new Date(updatedAt);
  const now = new Date();
  const hoursDiff = (now.getTime() - updated.getTime()) / (1000 * 60 * 60);
  return hoursDiff > DRAFT_TTL_HOURS;
}

/**
 * Get stored draft for a form if it exists and is not expired.
 */
export function getFormDraft(
  projectId: string,
  formId: string,
  version: number,
): StoredFormDraft | null {
  try {
    const key = getStorageKey(projectId, formId, version);
    const data = localStorage.getItem(key);
    if (!data) {
      return null;
    }

    const draft = JSON.parse(data) as StoredFormDraft;

    if (draft.schemaVersion !== 1) {
      clearFormDraft(projectId, formId, version);
      return null;
    }

    if (
      draft.projectId !== projectId ||
      draft.formId !== formId ||
      draft.version !== version
    ) {
      clearFormDraft(projectId, formId, version);
      return null;
    }

    if (isDraftExpired(draft.updatedAt)) {
      clearFormDraft(projectId, formId, version);
      return null;
    }

    return draft;
  } catch {
    clearFormDraft(projectId, formId, version);
    return null;
  }
}

/**
 * Store a draft for a form.
 */
export function setFormDraft(draft: StoredFormDraft): void {
  try {
    const key = getStorageKey(draft.projectId, draft.formId, draft.version);
    localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // localStorage unavailable or quota exceeded - silently fail
  }
}

/**
 * Clear stored draft for a form.
 */
export function clearFormDraft(
  projectId: string,
  formId: string,
  version: number,
): void {
  try {
    const key = getStorageKey(projectId, formId, version);
    localStorage.removeItem(key);
  } catch {
    // localStorage unavailable - silently fail
  }
}

export const formDraftStorage = {
  get: getFormDraft,
  set: setFormDraft,
  clear: clearFormDraft,
};

